/**
 * Cloud project save/load — full workspace snapshot in GeoVera backend JSONB.
 * Prefer real cloud saves; local storage is only a cache / explicit offline path.
 */
import { apiFetch, ensureCsrfToken } from './apiClient';
import type { Layer } from '../store/slices/layersSlice';
import type { BasemapType } from '../config/basemaps';
import type { Drawing, DrawingStyle, TextStyle } from '../types/drawing';

export interface ProjectListItem {
  id: string;
  name: string;
  description?: string;
  updatedAt: string;
  createdAt?: string;
}

export interface ProjectStateSnapshot {
  version: number;
  savedAt: string;
  view?: { center: [number, number]; zoom: number; rotation?: number };
  basemap?: BasemapType;
  activeLayerId?: string | null;
  layers?: Layer[];
  drawings?: Drawing[];
  drawingStyle?: DrawingStyle;
  textStyle?: TextStyle;
  ui?: {
    isNonTechnicalMode?: boolean;
  };
}

export function buildWorkspaceSnapshot(args: {
  layers: Layer[];
  activeLayerId: string | null;
  mapView: { center: [number, number]; zoom: number; rotation?: number };
  basemap: BasemapType;
  drawings?: Drawing[];
  drawingStyle?: DrawingStyle;
  textStyle?: TextStyle;
  isNonTechnicalMode?: boolean;
}): ProjectStateSnapshot {
  const {
    layers,
    activeLayerId,
    mapView,
    basemap,
    drawings,
    drawingStyle,
    textStyle,
    isNonTechnicalMode,
  } = args;

  return {
    version: 3,
    savedAt: new Date().toISOString(),
    view: {
      center: mapView.center,
      zoom: mapView.zoom,
      rotation: mapView.rotation ?? 0,
    },
    basemap,
    activeLayerId,
    layers: layers.map((l) => ({
      id: l.id,
      name: l.name,
      type: l.type,
      geometryType: l.geometryType,
      visible: l.visible,
      opacity: l.opacity,
      zIndex: l.zIndex,
      featureCount: l.featureCount ?? l.features?.length ?? 0,
      features: l.features ? structuredCloneSafe(l.features) : [],
      symbology: l.symbology ? structuredCloneSafe(l.symbology) : undefined,
      crs: l.crs,
      source: l.source,
      sourceType: l.sourceType,
      sourceUrl: l.sourceUrl,
      renderStyle: l.renderStyle,
      attributeFields: l.attributeFields,
    })),
    drawings: drawings ? structuredCloneSafe(drawings) : [],
    drawingStyle: drawingStyle ? structuredCloneSafe(drawingStyle) : undefined,
    textStyle: textStyle ? structuredCloneSafe(textStyle) : undefined,
    ui: {
      isNonTechnicalMode: !!isNonTechnicalMode,
    },
  };
}

function structuredCloneSafe<T>(value: T): T {
  try {
    return typeof structuredClone === 'function'
      ? structuredClone(value)
      : JSON.parse(JSON.stringify(value));
  } catch {
    return JSON.parse(JSON.stringify(value));
  }
}

/** Cookie authentication is server-owned; callers should use Redux state for UI gating. */
export function canSaveToCloud(): boolean {
  return true;
}

async function parseJson(res: Response): Promise<any> {
  const ct = res.headers.get('content-type') || '';
  if (!ct.includes('application/json')) {
    const text = await res.text().catch(() => '');
    throw new Error(
      res.status === 401
        ? 'Not signed in — please sign in to save to the cloud'
        : `Cloud API error (${res.status})${text ? `: ${text.slice(0, 120)}` : ''}`
    );
  }
  return res.json();
}

function cloudErrorMessage(json: any, status: number): string {
  if (json?.message) return String(json.message);
  if (typeof json?.error === 'string') return json.error;
  if (status === 401)
    return 'Not signed in — please sign in with Google to save projects to the cloud.';
  if (status === 403) {
    if (
      String(json?.error || '')
        .toLowerCase()
        .includes('csrf')
    ) {
      return 'Security check failed (CSRF). Refresh the page and try saving again.';
    }
    return json?.message || 'Not allowed to save this project.';
  }
  if (status === 413) return 'Project is too large to upload. Remove some layers and try again.';
  return `Cloud save failed (${status})`;
}

const LOCAL_PROJECTS_KEY = 'geovera_local_projects';

export interface LocalProject {
  id: string;
  name: string;
  description?: string;
  projectState: ProjectStateSnapshot;
  updatedAt: string;
  isOfflineFallback?: boolean;
}

export function getLocalProjects(): LocalProject[] {
  try {
    const raw = localStorage.getItem(LOCAL_PROJECTS_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

export function saveLocalProject(project: Omit<LocalProject, 'updatedAt'>): void {
  try {
    const projects = getLocalProjects();
    const existingIndex = projects.findIndex((p) => p.id === project.id);
    const updatedProject: LocalProject = {
      ...project,
      updatedAt: new Date().toISOString(),
    };
    if (existingIndex >= 0) {
      projects[existingIndex] = updatedProject;
    } else {
      projects.unshift(updatedProject);
    }
    // Cap local cache size
    localStorage.setItem(LOCAL_PROJECTS_KEY, JSON.stringify(projects.slice(0, 20)));
  } catch (e) {
    console.error('Failed to write to local storage', e);
  }
}

export async function listCloudProjects(): Promise<ProjectListItem[]> {
  const localList = getLocalProjects().map((p) => ({
    id: p.id,
    name: p.isOfflineFallback || p.id.startsWith('local_') ? `[Local] ${p.name}` : p.name,
    description:
      p.description || (p.id.startsWith('local_') ? 'Saved on this device only' : 'Local cache'),
    updatedAt: p.updatedAt,
    createdAt: p.updatedAt,
  }));

  try {
    await ensureCsrfToken(); // warm cookie for later POSTs
    const res = await apiFetch('/api/projects', {
      method: 'GET',
      credentials: 'include',
      timeoutMs: 15000,
    });
    const json = await parseJson(res);
    if (res.ok && json.success) {
      const cloudList = (json.data || []).map((p: any) => ({
        id: p.id,
        name: p.name,
        description: p.description,
        updatedAt: p.updatedAt || p.updated_at,
        createdAt: p.createdAt || p.created_at,
      }));
      // Local-only offline drafts first, then cloud
      const localOnly = localList.filter((p) => p.id.startsWith('local_'));
      return [...localOnly, ...cloudList];
    }
    if (res.status === 401) {
      return localList;
    }
  } catch (err) {
    console.warn('Cloud projects listing failed, falling back to local storage only:', err);
  }

  return localList;
}

/**
 * Save project to GeoVera cloud (Postgres).
 * Throws on auth / API errors so the UI can prompt sign-in — no silent "success" offline lie.
 */
export async function saveProjectToCloud(args: {
  name: string;
  description?: string;
  projectState: ProjectStateSnapshot;
  /** If set and is a cloud UUID, updates existing project */
  projectId?: string | null;
  /** Only use local storage if true AND network is down (default false) */
  allowOfflineFallback?: boolean;
}): Promise<{ id: string; name: string; message?: string; offline?: boolean }> {
  const name = args.name.trim();
  if (!name) {
    throw new Error('Project name is required');
  }

  // Force a fresh aligned CSRF cookie+header before large project POSTs
  await ensureCsrfToken(true);

  const body = {
    name,
    description: args.description || '',
    projectState: args.projectState,
    crs: 'EPSG:3857',
  };

  const cloudId = args.projectId && !args.projectId.startsWith('local_') ? args.projectId : null;

  const doRequest = async (method: 'POST' | 'PUT', path: string) => {
    // apiFetch attaches CSRF + retries once on invalid CSRF
    const res = await apiFetch(path, {
      method,
      credentials: 'include',
      body,
      // Large workspace snapshots need headroom
      timeoutMs: 60000,
      retry: false,
    });
    const json = await parseJson(res);
    return { res, json };
  };

  try {
    // Update existing cloud project
    if (cloudId) {
      const { res, json } = await doRequest('PUT', `/api/projects/${cloudId}`);
      if (res.ok && json.success) {
        saveLocalProject({
          id: cloudId,
          name,
          description: args.description,
          projectState: args.projectState,
        });
        return {
          id: json.data?.id || cloudId,
          name: json.data?.name || name,
          message: json.message || `Saved to cloud: “${name}”`,
        };
      }
      // Stale id → create new
      if (res.status === 404) {
        /* fall through to POST */
      } else {
        throw new Error(cloudErrorMessage(json, res.status));
      }
    }

    // Create new cloud project (also when previous id was local_*)
    const { res, json } = await doRequest('POST', '/api/projects');
    if (res.ok && json.success) {
      const newId = json.data?.id as string;
      if (newId) {
        saveLocalProject({
          id: newId,
          name,
          description: args.description,
          projectState: args.projectState,
        });
        // Drop local draft if we promoted one
        if (args.projectId?.startsWith('local_')) {
          try {
            const filtered = getLocalProjects().filter((p) => p.id !== args.projectId);
            localStorage.setItem(LOCAL_PROJECTS_KEY, JSON.stringify(filtered));
          } catch {
            /* ignore */
          }
        }
      }
      return {
        id: newId,
        name: json.data?.name || name,
        message: json.message || `Saved to cloud: “${name}”`,
      };
    }
    throw new Error(cloudErrorMessage(json, res.status));
  } catch (err) {
    const msg = err instanceof Error ? err.message : 'Cloud save failed';
    const networkish =
      /timed out|network|failed to fetch|Load failed|Cloud API connection|ECONN|abort/i.test(msg);

    if (args.allowOfflineFallback && networkish) {
      const localId = args.projectId?.startsWith('local_') ? args.projectId : `local_${Date.now()}`;
      saveLocalProject({
        id: localId,
        name,
        description: args.description,
        projectState: args.projectState,
        isOfflineFallback: true,
      });
      return {
        id: localId,
        name,
        offline: true,
        message:
          'Saved on this device only — cloud unreachable. Sign in and try again when online to sync.',
      };
    }

    throw err instanceof Error ? err : new Error(msg);
  }
}

export async function loadProjectFromCloud(projectId: string): Promise<{
  projectState: ProjectStateSnapshot;
  name: string;
}> {
  if (projectId.startsWith('local_')) {
    const local = getLocalProjects().find((p) => p.id === projectId);
    if (!local) {
      throw new Error('Local offline project not found in this browser.');
    }
    return {
      name: local.name,
      projectState: local.projectState,
    };
  }

  try {
    const res = await apiFetch(`/api/projects/${projectId}/full`, {
      method: 'GET',
      credentials: 'include',
      timeoutMs: 60000,
    });
    const json = await parseJson(res);
    if (res.ok && json.success && json.data) {
      return {
        name: json.data.name,
        projectState: (json.data.projectState || {}) as ProjectStateSnapshot,
      };
    }
    if (!res.ok) {
      throw new Error(cloudErrorMessage(json, res.status));
    }
  } catch (err) {
    const localCopy = getLocalProjects().find((p) => p.id === projectId);
    if (localCopy) {
      console.warn('Cloud load failed, using local cache:', err);
      return {
        name: localCopy.name,
        projectState: localCopy.projectState,
      };
    }
    throw err instanceof Error
      ? err
      : new Error('Could not load project from cloud and no local cache was found.');
  }

  throw new Error('Could not load project from cloud.');
}

export async function deleteCloudProject(projectId: string): Promise<void> {
  try {
    const projects = getLocalProjects();
    const filtered = projects.filter((p) => p.id !== projectId);
    localStorage.setItem(LOCAL_PROJECTS_KEY, JSON.stringify(filtered));
  } catch {
    /* ignore */
  }

  if (projectId.startsWith('local_')) {
    return;
  }

  const res = await apiFetch(`/api/projects/${projectId}`, {
    method: 'DELETE',
    credentials: 'include',
    timeoutMs: 15000,
    retry: false,
  });
  const json = await parseJson(res);
  if (!res.ok || !json.success) {
    throw new Error(json.message || json.error || 'Failed to delete cloud project');
  }
}

/** Remember last cloud project id for overwrite / autosave */
const LAST_PROJECT_KEY = 'geovera_last_cloud_project';

export function getLastCloudProject(): { id: string; name: string } | null {
  try {
    const raw = localStorage.getItem(LAST_PROJECT_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function setLastCloudProject(id: string, name: string): void {
  try {
    localStorage.setItem(LAST_PROJECT_KEY, JSON.stringify({ id, name }));
  } catch {
    /* ignore */
  }
}
